import type { EnvironmentProviders } from '@angular/core';
import { provideOktaAuth, withOktaConfig } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from '../../../environments/environment';
import { logEventqEnvironmentOnBoot, logOktaDebug } from './okta-debug-log';
import { eventqOktaRedirectUriFromEnv } from './okta-redirect';

/**
 * Builds the shared OktaAuth instance from environment.
 * Placeholder issuer/clientId keep bootstrap safe when Okta is not configured yet.
 */
export function createEventqOktaAuth(): OktaAuth {
  const issuerFallback = !environment.okta.issuer?.trim();
  const clientIdFallback = !environment.okta.clientId?.trim();
  const options = {
    issuer: environment.okta.issuer?.trim() || 'https://localhost/oauth2/default',
    clientId: environment.okta.clientId?.trim() || 'not-configured',
    redirectUri: eventqOktaRedirectUriFromEnv(),
    scopes: [...environment.okta.scopes],
    pkce: true,
    /** EventQ finishes STS on `/login/callback` and routes itself. */
    restoreOriginalUri: async () => undefined,
    /** Replace `/login` so Back after sign-in cannot return to the login document. */
    setLocation: (url: string) => {
      window.location.replace(url);
    },
  };

  logEventqEnvironmentOnBoot();
  logOktaDebug('createEventqOktaAuth: constructing OktaAuth', {
    issuerFallback,
    clientIdFallback,
    constructedOptions: options,
  });

  const oktaAuth = new OktaAuth(options);
  logOktaDebug('createEventqOktaAuth: OktaAuth constructed', {
    sdkOptions: {
      issuer: oktaAuth.options.issuer,
      clientId: oktaAuth.options.clientId,
      redirectUri: oktaAuth.options.redirectUri,
      scopes: oktaAuth.options.scopes,
      pkce: oktaAuth.options.pkce,
    },
  });
  return oktaAuth;
}

/** Okta Angular bootstrap providers (`provideOktaAuth` + env-based OktaAuth). */
export const EVENTQ_OKTA_PROVIDERS: EnvironmentProviders = provideOktaAuth(
  withOktaConfig({
    oktaAuth: createEventqOktaAuth(),
  }),
);
